import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BalanceCard, Button, EmptyState, ListGroup, ListRow, Skeleton, TextField } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { runwayProps } from '../runway.ts';
import { haptic } from '../telegram.ts';

/** A promo code adds its bonus to the balance right away. */
function PromoCode() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t, usd } = usePrefs();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const redeem = useMutation(
    trpc.promo.redeem.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setCode('');
        queryClient.invalidateQueries(trpc.balance.pathFilter());
      },
      onError: () => haptic.error(),
    }),
  );
  if (!open) {
    return (
      <ListGroup>
        <ListRow icon="gift" title={t.balance.promoRow} chevron onClick={() => setOpen(true)} />
      </ListGroup>
    );
  }
  return (
    <section className="ul-stack">
      <TextField
        id="promo"
        label={t.balance.promo}
        value={code}
        onChange={(v) => {
          setCode(v.toUpperCase());
          redeem.reset();
        }}
        placeholder={t.balance.promoPlaceholder}
        error={redeem.isError ? errorText(redeem.error) : undefined}
      />
      {redeem.isSuccess ? (
        <div className="ul-success-note" role="status">
          {t.balance.promoDone(usd(redeem.data.amountCents))}
        </div>
      ) : null}
      <Button
        variant="secondary"
        block
        disabled={!code.trim()}
        loading={redeem.isPending}
        onClick={() => redeem.mutate({ code: code.trim() })}
      >
        {t.balance.promoApply}
      </Button>
    </section>
  );
}

export function BalanceScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { t, money, usd, currency, ratesReady, date, dateTime } = usePrefs();
  const summary = useQuery(trpc.balance.summary.queryOptions());
  const history = useQuery(trpc.balance.history.queryOptions());

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">{t.balance.title}</h1>
      {summary.data ? (
        <BalanceCard
          amount={money(summary.data.balanceCents)}
          {...runwayProps(summary.data, { money, date, t })}
          approx={
            currency !== 'USD' && ratesReady ? t.balance.inUsd(usd(summary.data.balanceCents)) : undefined
          }
          onTopUp={() => nav.push({ name: 'topup' })}
        />
      ) : (
        <Skeleton height={180} />
      )}

      <PromoCode />

      {history.isPending ? (
        <Skeleton height={160} />
      ) : history.isError ? (
        <div className="ul-error" role="alert">
          {errorText(history.error)}
        </div>
      ) : history.data.length === 0 ? (
        <EmptyState icon="clock" title={t.balance.emptyTitle} text={t.balance.emptyText} />
      ) : (
        <ListGroup header={t.balance.history}>
          {history.data.map((h) => (
            <ListRow
              key={h.id}
              icon={h.amountCents > 0 ? 'arrowDown' : 'arrowUp'}
              title={h.title}
              subtitle={[dateTime(h.createdAt), h.asset].filter(Boolean).join(' · ')}
              value={
                <span className={h.amountCents > 0 ? 'ul-history-amount--in' : undefined}>
                  {h.amountCents > 0 ? '+' : ''}
                  {money(h.amountCents)}
                </span>
              }
            />
          ))}
        </ListGroup>
      )}
    </main>
  );
}
